/**
 * The client category list (#604), shared by the two forms that offer it: New
 * licence (`add-licence`) and Edit licence (`licence-detail`).
 *
 * The list lives in the database (`GET /licences/client-categories`): each form
 * loads it when it opens, and an Administrator can add a category from the
 * select itself (`POST /licences/client-categories`). Both slices hold the same
 * `ClientCategoriesState` and move it with the same transitions below, so the
 * lifecycle is written (and tested) once.
 *
 * Pure (no React, no MUI, no fetch). The transitions mutate the draft they are
 * given, which is what an RTK reducer hands them.
 */

import type { ClientCategory } from '@shared/schemas';

export type { ClientCategory };

export type ClientCategoriesStatus = 'idle' | 'loading' | 'loaded' | 'failed';

/** The "+ Add a category" request, as the select shows it. */
export interface ClientCategoryAddState {
  status: 'idle' | 'pending' | 'failed';
  /** The server's refusal ("… is already in the list."), shown under the input. */
  error: string | null;
  /** The category the last successful add created; the form picks it. */
  added: ClientCategory | null;
}

export interface ClientCategoriesState {
  items: ClientCategory[];
  status: ClientCategoriesStatus;
  add: ClientCategoryAddState;
}

export const emptyClientCategories: ClientCategoriesState = {
  items: [],
  status: 'idle',
  add: { status: 'idle', error: null, added: null },
};

/** The state transitions both slices delegate to. */
export const clientCategoriesTransitions = {
  /** A form opening: a refusal or an add left over from its previous opening is forgotten. */
  loadStarted(state: ClientCategoriesState) {
    state.status = 'loading';
    state.add = { status: 'idle', error: null, added: null };
  },
  loadSucceeded(state: ClientCategoriesState, items: ClientCategory[]) {
    state.items = items;
    state.status = 'loaded';
  },
  /** The select says it could not load; a category already picked stays shown. */
  loadFailed(state: ClientCategoriesState) {
    state.status = 'failed';
  },
  addStarted(state: ClientCategoriesState) {
    state.add = { status: 'pending', error: null, added: null };
  },
  /** The new category closes the list, like the server's `sort_order` puts it. */
  addSucceeded(state: ClientCategoriesState, category: ClientCategory) {
    if (!state.items.some((c) => c.value === category.value)) state.items.push(category);
    state.add = { status: 'idle', error: null, added: category };
  },
  addFailed(state: ClientCategoriesState, error: string) {
    state.add = { status: 'failed', error, added: null };
  },
  /** The input was cancelled or edited: a refusal about the previous text no longer applies. */
  addCleared(state: ClientCategoriesState) {
    state.add = { status: 'idle', error: null, added: state.add.added };
  },
};

/**
 * The options a form offers: the loaded list, plus the category the licence
 * already has when the list does not (yet) hold it, so opening Edit licence
 * never shows a blank field while the list loads, nor if it fails to.
 */
export function clientCategoryOptions(
  items: readonly ClientCategory[],
  current: ClientCategory | null,
): readonly ClientCategory[] {
  if (!current || items.some((c) => c.value === current.value)) return items;
  return [current, ...items];
}

/** The licence's category as an option, or `null` when it has none (older licences). */
export function currentClientCategory(licence: {
  client_category: string | null;
  client_category_label: string | null;
}): ClientCategory | null {
  return licence.client_category
    ? { value: licence.client_category, label: licence.client_category_label ?? licence.client_category }
    : null;
}
